import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Link, Typography } from "@mui/material"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { StandardAlertInfo } from "../../../../alert/StandardAlertInfo"

import type { TakeawayKitchen } from "../takeawayTypes"
import { isValidApiString } from "./takeawayCheckoutLib"
import { useApiImageUrl } from "../useApiImageUrl"

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        width: "32rem",
        maxWidth: "32rem",
        "& [class*=dialogTopbar]": {
            paddingBottom: "0",
        },
    },
    dialogTitleContainer: {
        "& .MuiTypography-h4": {
            fontSize: "1.5625rem",
            fontWeight: 600,
            lineHeight: 1,
            color: theme.palette.tailwind.slate[700],
            marginBottom: "1rem",
        },
    },
    dialogContent: {
        "&.MuiDialogContent-root": {
            padding: "1rem 2.5rem 1.5rem",
        },
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    heroImage: {
        width: "100%",
        height: "10rem",
        objectFit: "cover",
        display: "block",
    },
    heroPlaceholder: {
        width: "100%",
        height: "10rem",
        background: theme.palette.tailwind.slate[200],
    },
    description: {
        color: theme.palette.tailwind.slate[950],
    },
    sectionHeader: {
        color: theme.palette.tailwind.slate[950],
    },
    addressLine: {
        color: theme.palette.tailwind.slate[950],
    },
    row: {
        color: theme.palette.tailwind.slate[950],
    },
    contactRows: {
        display: "flex",
        flexDirection: "column",
        gap: ".625rem",
    },
    openingHoursAlert: {
        width: "100%",
    },
    link: {
        color: theme.palette.tailwind.blue[500],
        textDecoration: "none",
    },
}))

type TakeawayStoreDetailsDialogProps = Readonly<{
    kitchen: TakeawayKitchen
    onClose: () => void
}>

export function TakeawayStoreDetailsDialog({ kitchen, onClose }: TakeawayStoreDetailsDialogProps) {
    const { classes } = useStyles()
    const heroImageUrl = useApiImageUrl(kitchen.brandingDetails?.imageUrl)

    const phoneNumber = isValidApiString(kitchen.phoneNumber) ? kitchen.phoneNumber : undefined
    const email = isValidApiString(kitchen.email) ? kitchen.email : undefined
    const openingHours = isValidApiString(kitchen.brandingDetails?.openingHours)
        ? kitchen.brandingDetails!.openingHours
        : undefined

    const address = [kitchen.streetName, kitchen.streetNumber].filter(Boolean).join(" ")
    const city = [kitchen.postalCode, kitchen.city].filter(Boolean).join(" ")

    const hasAddress = !!(address || city)
    const hasContact = !!(openingHours || phoneNumber || email)

    return (
        <StandardDialog
            open
            onClose={onClose}
            dialogTitle={kitchen.name}
            maxWidthProp="sm"
            updateDialogClasses={{ dialogTitleContainer: classes.dialogTitleContainer }}
            slotProps={{ paper: { className: classes.dialogPaper } }}
        >
            {heroImageUrl ? (
                <img src={heroImageUrl} className={classes.heroImage} alt={kitchen.name} />
            ) : (
                <Box className={classes.heroPlaceholder} />
            )}

            <DialogContent className={classes.dialogContent}>
                <Box className={classes.content}>
                    {kitchen.brandingDetails?.description && (
                        <Typography variant="body1" className={classes.description}>
                            {kitchen.brandingDetails.description}
                        </Typography>
                    )}

                    {openingHours && (
                        <LocalizedStrict
                            id="takeaway-store-details-opening-hours-banner"
                            vars={{ hours: openingHours }}
                            attrs={{ title: true }}
                        >
                            <StandardAlertInfo title="" severity="success" className={classes.openingHoursAlert} />
                        </LocalizedStrict>
                    )}

                    {hasAddress && (
                        <>
                            <Box>
                                <LocalizedStrict id="takeaway-store-details-address">
                                    <Typography variant="subtitle1" component="div" className={classes.sectionHeader}>
                                        Address
                                    </Typography>
                                </LocalizedStrict>
                                {address && (
                                    <Typography variant="body1" className={classes.addressLine}>
                                        {address}
                                    </Typography>
                                )}
                                {city && (
                                    <Typography variant="body1" className={classes.addressLine}>
                                        {city}
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}

                    {hasContact && (
                        <Box>
                            <LocalizedStrict id="takeaway-store-details-contact">
                                <Typography variant="subtitle1" component="div" className={classes.sectionHeader}>
                                    Contact
                                </Typography>
                            </LocalizedStrict>
                            <Box className={classes.contactRows}>
                                {openingHours && (
                                    <Typography variant="body1" className={classes.row}>
                                        {openingHours}
                                    </Typography>
                                )}
                                {phoneNumber && (
                                    <Link href={`tel:${phoneNumber}`} className={classes.link}>
                                        {phoneNumber}
                                    </Link>
                                )}
                                {email && (
                                    <Link href={`mailto:${email}`} className={classes.link}>
                                        {email}
                                    </Link>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </StandardDialog>
    )
}
